import { QContentStatusTag, QuestionTypeChip } from '@exam-management/components';
import {
  QuestionGroupType,
  QuestionStatus,
  type QuestionKeywordSearchItemDto,
} from '@exam-management/graphql';
import {
  Box,
  CheckRoundedIcon,
  Chip,
  ContentPreviewer,
  Divider,
  Flex,
  Text,
} from '@shared/components';
import { formatDateTime } from '@shared/utils';
import { useMemo } from 'react';

import { getDifficultyText, sanitizeContentHtml } from './utils';

const VALID_STATUSES = new Set<string>(Object.values(QuestionStatus));

interface SearchResultCardProps {
  item: QuestionKeywordSearchItemDto;
}

function getQTypeChip(groupType: string, questionSize: number | null) {
  if (groupType === QuestionGroupType.Single) {
    return <QuestionTypeChip type='single' />;
  } else if (groupType === QuestionGroupType.ItemSet) {
    return <QuestionTypeChip type='item-set' questionSize={questionSize} />;
  } else if (groupType === QuestionGroupType.LotOfQuestion) {
    return <QuestionTypeChip type='single' />;
  }
  return null;
}

function getQuestionDetailPath(item: QuestionKeywordSearchItemDto): string {
  const { id, refQBankStructureId, questionGroupId, questionGroup } = item;
  if (questionGroup?.type === QuestionGroupType.ItemSet) {
    return `/qbank-structures/${refQBankStructureId}/qcontents/item-set/${questionGroupId}/${id}`;
  }
  return `/qbank-structures/${refQBankStructureId}/qcontents/single/${id}`;
}

export default function SearchResultCard({ item }: Readonly<SearchResultCardProps>) {
  const {
    refCode,
    status,
    updatedAt,
    details,
    tags,
    metadata,
    correctAnswer,
    solution,
    questionGroup,
  } = item;

  const lastModified = formatDateTime(updatedAt ? new Date(updatedAt) : undefined);

  const paragraph = questionGroup?.paragraph;
  const qTypeChip = questionGroup ? getQTypeChip(questionGroup.type, questionGroup.size) : null;

  const validStatus = VALID_STATUSES.has(status) ? (status as QuestionStatus) : null;
  const difficultyText = getDifficultyText(tags);
  const cleanDetails = useMemo(() => sanitizeContentHtml(details), [details]);
  const cleanParagraph = useMemo(
    () => (paragraph ? sanitizeContentHtml(paragraph) : null),
    [paragraph],
  );
  const cleanSolution = useMemo(
    () => (solution?.value ? sanitizeContentHtml(solution.value) : null),
    [solution?.value],
  );
  const cleanReference = useMemo(
    () => (metadata?.reference ? sanitizeContentHtml(metadata.reference) : null),
    [metadata?.reference],
  );
  const cleanChoices = useMemo(
    () =>
      metadata.choices
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((choice) => ({ ...choice, label: sanitizeContentHtml(choice.label) })),
    [metadata.choices],
  );

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: theme.shape.rounded(2),
        overflow: 'hidden',
      })}
    >
      <Flex
        sx={(theme) => ({
          px: theme.spacing(6),
          py: theme.spacing(4),
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          gap: theme.spacing(2),
        })}
      >
        <Chip
          label={refCode}
          component='a'
          href={getQuestionDetailPath(item)}
          target='_blank'
          rel='noopener noreferrer'
          clickable
        />
        <Flex sx={(theme) => ({ gap: theme.spacing(2), alignItems: 'center' })}>
          <Flex
            sx={(theme) => ({
              alignItems: 'center',
              gap: theme.spacing(2),
            })}
          >
            <Text variant='body2'>สถานะคำถาม:</Text>
            {validStatus ? (
              <QContentStatusTag status={validStatus} />
            ) : (
              <Text variant='body2'>{status}</Text>
            )}
          </Flex>
          <Divider orientation='vertical' flexItem />
          <Text variant='body2' color='text.secondary'>
            Last Modified: {lastModified}
          </Text>
        </Flex>
      </Flex>

      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(4),
          p: theme.spacing(6),
          // single source of size for every rendered content field — the HTML views inherit it
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
          // stored content can carry heading tags and KaTeX (.katex is 1.21em), both of which
          // survive sanitizeContentHtml because they are not font-size declarations
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            fontSize: 'inherit',
            lineHeight: 'inherit',
          },
          '& .katex': { fontSize: 'inherit' },
        })}
      >
        {cleanParagraph ? (
          <Box>
            <Text variant='body1Semibold' sx={(theme) => ({ mb: theme.spacing(1) })}>
              Paragraph
            </Text>
            <Box
              sx={(theme) => ({
                bgcolor: theme.palette.grey[100],
                px: theme.spacing(3.5),
                py: theme.spacing(2.5),
                borderRadius: theme.shape.rounded(1),
              })}
            >
              <ContentPreviewer content={cleanParagraph} />
            </Box>
          </Box>
        ) : null}

        {cleanParagraph ? <Divider /> : null}

        <Box>
          <Flex
            sx={(theme) => ({
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: theme.spacing(1),
              flexWrap: 'wrap',
              gap: theme.spacing(1),
            })}
          >
            <Text variant='body1Semibold'>คำถาม</Text>
            <Flex sx={(theme) => ({ gap: theme.spacing(2), alignItems: 'center' })}>
              <Text variant='body2' color='text.secondary'>
                ระดับความยาก: {difficultyText}
              </Text>
              {qTypeChip ? (
                <Flex sx={(theme) => ({ gap: theme.spacing(1), alignItems: 'center' })}>
                  <Text variant='body2' color='text.secondary'>
                    QType:
                  </Text>
                  {qTypeChip}
                </Flex>
              ) : null}
            </Flex>
          </Flex>
          <Box
            sx={(theme) => ({
              bgcolor: theme.palette.grey[100],
              p: theme.spacing(4),
              borderRadius: theme.shape.rounded(2),
            })}
          >
            <ContentPreviewer content={cleanDetails} />
          </Box>
        </Box>

        {cleanChoices.length > 0 ? (
          <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
            {cleanChoices.map((choice) => {
              const isCorrect =
                correctAnswer.value !== null && String(choice.value) === correctAnswer.value;
              return (
                <Flex
                  key={choice.order}
                  sx={(theme) => ({
                    gap: theme.spacing(3),
                    py: theme.spacing(3),
                    px: theme.spacing(4),
                    alignItems: 'center',
                    borderRadius: theme.shape.rounded(2),
                    bgcolor: isCorrect ? 'success.lighter' : 'transparent',
                  })}
                >
                  {isCorrect ? (
                    <CheckRoundedIcon
                      sx={(theme) => ({
                        color: theme.palette.success.main,
                        fontSize: 16,
                        mt: '2px',
                        flexShrink: 0,
                      })}
                    />
                  ) : (
                    <Box sx={{ width: 16, flexShrink: 0 }} />
                  )}
                  <Text sx={{ flexShrink: 0 }}>{choice.order}.</Text>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <ContentPreviewer content={choice.label} />
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        ) : null}

        {cleanSolution ? (
          <>
            <Divider />
            <Box>
              <Text variant='body1Semibold' sx={(theme) => ({ mb: theme.spacing(1) })}>
                คำอธิบายเฉลย
              </Text>
              <Box
                sx={(theme) => ({
                  bgcolor: theme.palette.grey[100],
                  px: theme.spacing(3.5),
                  py: theme.spacing(2.5),
                  borderRadius: theme.shape.rounded(1),
                })}
              >
                <ContentPreviewer content={cleanSolution} />
              </Box>
            </Box>
          </>
        ) : null}

        {cleanReference ? (
          <>
            <Divider />
            <Box>
              <Text variant='body1Semibold' sx={(theme) => ({ mb: theme.spacing(1) })}>
                หนังสืออ้างอิง
              </Text>
              <Box
                sx={(theme) => ({
                  bgcolor: theme.palette.grey[100],
                  px: theme.spacing(3.5),
                  py: theme.spacing(2.5),
                  borderRadius: theme.shape.rounded(1),
                })}
              >
                <ContentPreviewer content={cleanReference} />
              </Box>
            </Box>
          </>
        ) : null}
      </Flex>
    </Box>
  );
}
